import React, { useCallback, useState } from 'react';
import { SearchInput } from '@mds/ui';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import ArticlePreviewList from '../../ui/ArticlePreviewList';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useActivityLogger } from '../../hooks/use-activity-logger';

interface SearchForm {
    searchValue: string;
}

const formErrorMessage = 'Please type at least 2 characters';

const SearchPage: React.FC = () => {
    const [sendText, setSendText] = useState<string>('');
    const [articlesCount, setArticlesCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    useActivityLogger('Search', 'Search');

    const initialValues: SearchForm = {
        searchValue: '',
    };

    const { handleSubmit, handleBlur, setFieldValue, values, errors, touched } =
        useFormik<SearchForm>({
            initialValues,
            validationSchema: Yup.object({
                searchValue: Yup.string().min(2, formErrorMessage),
            }),
            onSubmit: ({ searchValue }: SearchForm) => {
                if (sendText === searchValue) {
                    return;
                }
                setSendText(searchValue);
                if (!searchValue) {
                    return;
                }
                setLoading(true);
            },
        });

    const handleSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue('searchValue', event.target.value);
        },
        [setFieldValue]
    );

    const onDataLoadedHandler = useCallback((count: number) => {
        setLoading(false);
        setArticlesCount(count);
    }, []);

    const articleCaption =
        articlesCount === 0
            ? `No articles for '${sendText}'`
            : `${articlesCount} ${articlesCount > 1 ? 'articles' : 'article'} found for '${sendText}'`;
    const articleSubcaption =
        articlesCount === 0
            ? 'No articles were found based on your search query'
            : 'Your search results';

    return (
        <form onSubmit={handleSubmit} autoComplete="off">
            <Grid container spacing={2}>
                <Grid
                    sx={{ py: 1, mb: 4 }}
                    size={{
                        xs: 12,
                        lg: 4,
                        sm: 6,
                        md: 6,
                    }}
                >
                    <Stack spacing={2}>
                        <Stack spacing={1} id="search-header">
                            <Typography variant="h5" component="h1">
                                Search and explore
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                Search within all articles in the Neon Insights
                                platform
                            </Typography>
                        </Stack>
                        <SearchInput
                            fullWidth
                            name="searchValue"
                            value={values.searchValue}
                            error={
                                !!(errors.searchValue && touched.searchValue)
                            }
                            placeholder="Search in article name or content"
                            helperText={
                                errors.searchValue && touched.searchValue
                                    ? errors.searchValue
                                    : undefined
                            }
                            onBlur={handleBlur}
                            onChange={handleSearchChange}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            startIcon={
                                loading ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : undefined
                            }
                        >
                            Search articles
                        </Button>
                    </Stack>
                </Grid>
                {!!sendText && (
                    <Grid
                        sx={{ display: loading ? 'none' : undefined }}
                        size={12}
                    >
                        <Stack spacing={1} id="result-header" sx={{ mb: 2 }}>
                            <Typography variant="h5" component="h2">
                                {articleCaption}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                {articleSubcaption}
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                display:
                                    sendText && articlesCount > 0
                                        ? undefined
                                        : 'none',
                            }}
                        >
                            <ArticlePreviewList
                                source="SearchArticle"
                                onDataLoaded={onDataLoadedHandler}
                                searchQuery={sendText}
                            />
                        </Box>
                    </Grid>
                )}
            </Grid>
        </form>
    );
};

export default SearchPage;
